import React, { useEffect, useState } from 'react';
import { FiHeadphones, FiArrowDown, FiExternalLink } from 'react-icons/fi';
import report from '../data/season-preview.json';
import styles from '../styles/season-preview.module.css';

const slug = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
// The report is rendered as text and semantic elements; no raw HTML is executed.
const inline = (text: string): React.ReactNode[] => text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\(https?:\/\/[^\s)]+\)|\*[^*]+\*)/g).map((part, i) => {
  const link = part.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
  if (link) return <a key={i} href={link[2]} target="_blank" rel="noreferrer">{link[1]}</a>;
  if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
  if (part.startsWith('*') && part.endsWith('*')) return <em key={i}>{part.slice(1, -1)}</em>;
  return part;
});
const blocks = report.markdown.split(/\r?\n\s*\r?\n/).slice(2);
const headings = blocks.filter(b => /^## /.test(b)).map(b => b.slice(3));

function ReportBlock({ block }: { block: string }) {
  if (block === '---') return <hr />;
  if (block.startsWith('### ')) {
    const title = block.slice(4);
    return <h3 id={slug(title)}>{title}</h3>;
  }
  if (block.startsWith('## ')) {
    const title = block.slice(3);
    return <h2 id={slug(title)}>{title}</h2>;
  }
  if (block.startsWith('|')) {
    const rows = block.split(/\r?\n/).filter(row => !/^\|[\s:|-]+\|$/.test(row)).map(row => row.split('|').slice(1, -1).map(cell => cell.trim()));
    return <div className={styles.tableScroll} tabIndex={0} role="region" aria-label="Preseason rankings"><table><caption>2026 preseason rankings</caption><thead><tr>{rows[0].map((cell, i) => <th scope="col" key={i}>{cell}</th>)}</tr></thead><tbody>{rows.slice(1).map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j}>{inline(cell)}</td>)}</tr>)}</tbody></table></div>;
  }
  if (block.startsWith('- ')) return <ul>{block.split(/\r?\n/).map((line, i) => <li key={i}>{inline(line.replace(/^- /, ''))}</li>)}</ul>;
  return <p>{inline(block)}</p>;
}

export default function SeasonPreview() {
  const [audioError, setAudioError] = useState(false);
  const { podcast } = report;
  useEffect(() => {
    const previous = document.title;
    document.title = '2026 Season Preview | Pocket Pigskin Association';
    return () => { document.title = previous; };
  }, []);
  return <div className={styles.page}>
    <header className={styles.hero}>
      <p className={styles.eyebrow}>Pocket Pigskin Association <span>2026 Season Preview</span></p>
      <h1>The chase.<br />The challengers.<br /><em>The basement.</em></h1>
      <div className={styles.byline}><time dateTime="2026-09-09">September 9, 2026</time><span>16 teams. Eight playoff spots.</span></div>
      <a className={styles.readLink} href="#season-report">Read the season outlook <FiArrowDown aria-hidden="true" /></a>
    </header>
    <div className={styles.layout}>
      <aside className={styles.sidebar}>
        <section className={styles.podcast} aria-labelledby="podcast-title">
          <FiHeadphones className={styles.headphones} aria-hidden="true" />
          <p className={styles.eyebrow}>Listen to the preview</p>
          <h2 id="podcast-title">{podcast.title}</h2>
          {podcast.audioUrl ? <>
            <audio controls preload="none" src={podcast.audioUrl} onError={() => setAudioError(true)} aria-label={podcast.title}>Your browser does not support audio playback.</audio>
            {audioError && <p role="status">The episode could not load. Try opening the audio directly.</p>}
            <a href={podcast.episodeUrl || podcast.audioUrl} target="_blank" rel="noreferrer">Open episode <FiExternalLink aria-hidden="true" /></a>
          </> : <><span className={styles.status}>Episode coming soon</span><p>The season preview podcast is on its way. Read the full outlook below while we get the episode ready.</p></>}
        </section>
        <nav className={styles.contents} aria-label="Season preview sections"><p className={styles.eyebrow}>In this preview</p>{headings.map((heading, i) => <a key={heading} href={`#${slug(heading)}`}><span>{String(i + 1).padStart(2, '0')}</span>{heading}</a>)}</nav>
      </aside>
      <article className={styles.article} id="season-report" aria-label="2026 PPA season outlook">{blocks.map((block, i) => <ReportBlock key={i} block={block} />)}</article>
    </div>
  </div>;
}
